import type { ReactNode } from 'react';
import { useAppContext } from '@/Main';
import { BrandFavicon } from '../BrandFavicon';
import { RelationshipBadge } from '../visibility/VisibilityBadges';
import { resolveAiMentions, formatAiUsagePercent } from '../../utils/visibility-metrics';
import type { ProjectVisibilityTopBrand } from '../../types';
import { MiniTrendSparkline } from './MiniTrendSparkline';

function MetricValue({
  value,
  width = '4.5rem',
  children,
}: {
  value: string | number;
  width?: string;
  children?: ReactNode;
}) {
  return (
    <div className="d-flex flex-shrink-0 flex-column align-items-center justify-content-center gap-1" style={{ width }}>
      <bdi className="fs--1 fw-bold text-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</bdi>
      {children}
    </div>
  );
}

function VisibilityValue({ score }: { score: number }) {
  return (
    <MetricValue value={`${score}%`}>
      <div className="rounded-pill bg-body-secondary overflow-hidden" style={{ height: '0.25rem', width: '3.5rem' }}>
        <div
          className="h-100 rounded-pill bg-primary"
          style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
        />
      </div>
    </MetricValue>
  );
}

export function DashboardTopBrandRow({
  brand,
  rank,
  totalExecutions,
}: {
  brand: ProjectVisibilityTopBrand;
  rank: number;
  totalExecutions: number;
}) {
  const {
    config: { themeVariant },
  } = useAppContext() as { config: { themeVariant?: string } };
  const lightPrimary = themeVariant === 'primary';

  return (
    <li className="d-flex align-items-center gap-3 rounded-3 border bg-body-tertiary px-3 py-2">
      <span
        className={`d-flex flex-shrink-0 align-items-center justify-content-center rounded-circle bg-primary fs--2 fw-bold ${lightPrimary ? '' : 'text-white'}`}
        style={{
          width: '1.75rem',
          height: '1.75rem',
          ...(lightPrimary ? { color: 'var(--falcon-primary-text-emphasis)' } : {}),
        }}
      >
        {rank}
      </span>

      <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
        <BrandFavicon
          host={brand.domain}
          faviconUrl={brand.faviconUrl}
          name={brand.canonicalName}
          relationship={brand.relationship}
        />
        <p className="text-truncate fs--1 fw-semibold text-1000 mb-0">{brand.canonicalName}</p>
        <RelationshipBadge
          relationship={brand.relationship}
          solid={brand.relationship === 'known'}
          className="fs--2 fw-normal"
        />
      </div>

      <div className="d-flex flex-shrink-0 align-items-center gap-4 gap-sm-5">
        <VisibilityValue score={brand.visibilityScore} />
        <MetricValue width="4rem" value={formatAiUsagePercent(resolveAiMentions(brand), totalExecutions)} />
        <MetricValue width="3.5rem" value={resolveAiMentions(brand)} />
        <MetricValue
          width="4.5rem"
          value={brand.avgPosition != null ? brand.avgPosition : '—'}
        />
        <MiniTrendSparkline changePercentage={brand.changePercentage} />
      </div>
    </li>
  );
}
